/**
 * Object Merged Event Component
 *
 * Handles ahoy_object_merged events.
 * Display pattern: "**Actor** merged **Contact A** into this contact"
 */

import {
  ActorName,
  bold,
  InlineText,
  TimelineEvent,
} from '../components/shared';
import type { ObjectLifecyclePayload } from '../types';
import { useEventFormatters } from '../useEventFormatters';
import type { EventComponentProps } from './types';

export function ObjectMergedEvent({ event, entityType }: EventComponentProps) {
  const { getActorInfo } = useEventFormatters();
  const actor = getActorInfo(event);
  const payload = event.payload as ObjectLifecyclePayload | undefined;

  // Format entity type for display (e.g., "contact_entity" → "contact")
  const entityTypeLabel = entityType
    .replace(/^ahoy_/, '')
    .replace(/_entity$/, '')
    .toLowerCase();

  // Get the source object label (the object that was merged into this one)
  const sourceLabel = payload?.label || 'another record';

  const ariaLabel = `${actor.name} merged ${sourceLabel} into this ${entityTypeLabel}`;

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Header timestamp={event.occurred_at}>
        <InlineText>
          <ActorName actor={actor} /> merged {bold(sourceLabel)} into this{' '}
          {entityTypeLabel}
        </InlineText>
      </TimelineEvent.Header>
    </TimelineEvent>
  );
}
